/* global interfaceConfig */

import { Checkbox } from '@atlaskit/checkbox';
import Spinner from '@atlaskit/spinner';
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { result } from 'lodash/object';
import React from 'react';

import { loginService, userService } from '../../../../service';
import { goToPage } from '../../../../utils';
import { ROOT } from '../../base/config/constants';
import { isMobileBrowser } from '../../base/environment/utils';
import { translate, translateToHTML } from '../../base/i18n';
import {
    Icon,
    IconEyeHide,
    IconEyeShow,
    IconLogoPanther,
    IconWarning
} from '../../base/icons';
import { connect } from '../../base/redux';
import { CalendarList } from '../../calendar-sync';
import SessionChecking from '../../conference/components/web/SessionChecking';
import { RecentList } from '../../recent-list';
import { setUserInfo } from '../../settings';

import { AbstractWelcomePage, _mapStateToProps } from './AbstractWelcomePage';
import Tabs from './Tabs';

/**
 * The pattern used to validate room name.
 *
 * @type {string}
 */
export const ROOM_NAME_VALIDATE_PATTERN_STR = '^[^?&:\u0022\u0027%#]+$';

/**
 * The Web container rendering the welcome page.
 *
 * @augments AbstractWelcomePage
 */
class WelcomePage extends AbstractWelcomePage {
    /**
     * Default values for {@code WelcomePage} component's properties.
     *
     * @static
     */
    static defaultProps = {
        _room: ''
    };

    /**
     * Initializes a new WelcomePage instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props) {
        super(props);

        this.state = {
            ...this.state,
            isLogin: false,
            hideMenu: true,
            isOpen: false,
            popUp: '',
            isOpenSchedCall: false,
            isNotDisturb: false,
            isAddContact: false,
            isKeepLoggedIn: false,
            isLoading: false,
            error: '',
            isShowLogin: false,
            isShowPassword: false,
            generateRoomnames:
                interfaceConfig.GENERATE_ROOMNAMES_ON_WELCOME_PAGE,
            selectedTab: 0
        };

        /**
         * The HTML Element used as the container for additional content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentRef = null;

        this._roomInputRef = null;

        /**
         * The HTML Element used as the container for additional toolbar content. Used
         * for directly appending the additional content template to the dom.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentRef = null;

        this._additionalCardRef = null;

        /**
         * The template to use as the additional card displayed near the main one.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalCardTemplate = document.getElementById(
            'welcome-page-additional-card-template');

        /**
         * The template to use as the main content for the welcome page. If
         * not found then only the welcome page head will display.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalContentTemplate = document.getElementById(
            'welcome-page-additional-content-template');

        /**
         * The template to use as the additional content for the welcome page header toolbar.
         * If not found then only the settings icon will be displayed.
         *
         * @private
         * @type {HTMLTemplateElement|null}
         */
        this._additionalToolbarContentTemplate = document.getElementById(
            'settings-toolbar-additional-content-template'
        );

        // Bind event handlers so they are only bound once per instance.
        this._onFormSubmit = this._onFormSubmit.bind(this);
        this._onRoomChange = this._onRoomChange.bind(this);
        this._onPasswordChange = this._onPasswordChange.bind(this);
        this._setAdditionalCardRef = this._setAdditionalCardRef.bind(this);
        this._setAdditionalContentRef
            = this._setAdditionalContentRef.bind(this);
        this._setRoomInputRef = this._setRoomInputRef.bind(this);
        this._setAdditionalToolbarContentRef
            = this._setAdditionalToolbarContentRef.bind(this);
        this._onTabSelected = this._onTabSelected.bind(this);
        this._renderFooter = this._renderFooter.bind(this);
        this.createLoginEvent = this.createLoginEvent.bind(this);
        this.getUser = this.getUser.bind(this);
    }

    /**
     * Implements React's {@link Component#componentDidMount()}. Invoked
     * immediately after this component is mounted.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentDidMount() {
        super.componentDidMount();

        document.body.classList.add('welcome-page');
        document.title = interfaceConfig.APP_NAME;

        if (this.state.generateRoomnames) {
            this._updateRoomname();
        }

        if (this._shouldShowAdditionalContent()) {
            this._additionalContentRef.appendChild(
                this._additionalContentTemplate.content.cloneNode(true));
        }

        if (this._shouldShowAdditionalToolbarContent()) {
            this._additionalToolbarContentRef.appendChild(
                this._additionalToolbarContentTemplate.content.cloneNode(true)
            );
        }

        if (this._shouldShowAdditionalCard()) {
            this._additionalCardRef.appendChild(
                this._additionalCardTemplate.content.cloneNode(true)
            );
        }

        const token = jitsiLocalStorage.getItem('token');

        if (token) {
            this.getUser();
            goToPage(ROOT);
        }
    }

    /**
     * Removes the classname used for custom styling of the welcome page.
     *
     * @inheritdoc
     * @returns {void}
     */
    componentWillUnmount() {
        super.componentWillUnmount();

        document.body.classList.remove('welcome-page');
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement|null}
     */
    render() {
        const { _moderatedRoomServiceUrl, t } = this.props;
        const { isNotDisturb } = this.state;
        const { DISPLAY_WELCOME_FOOTER } = interfaceConfig;
        const showAdditionalContent = this._shouldShowAdditionalContent();
        const contentClassName = showAdditionalContent ? 'with-content' : 'without-content';
        const footerClassName = DISPLAY_WELCOME_FOOTER ? 'with-footer' : 'without-footer';
        const disabled = this.state.room && this.state.password;

        return (
            <div
                className = { `welcome ${contentClassName} ${footerClassName} ${isNotDisturb && 'dnd'}` }
                id = 'welcome_page'>
                <div
                    style = {{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        height: '100%',
                        width: '100%',
                        position: 'absolute'
                    }}>
                    <div className = { `header ${this.state.isShowLogin ? 'open' : ''}` }>
                        <div className = 'dis-flex jus-end'>
                            <button
                                className = { 'toggle-show-login' }
                                onClick = { () => this.setState({ isShowLogin: !this.state.isShowLogin }) }>Log in</button>
                            <br />
                        </div>
                        {
                            this.state.isShowLogin && <div className = 'header-container'>
                                <div id = 'enter_room'>
                                    <p className = 'enter-room-title-input'>Email</p>
                                    <div className = 'enter-room-input-container'>
                                        <input
                                            aria-disabled = 'false'
                                            aria-label = 'Meeting name input'
                                            autoFocus = { true }
                                            className = 'enter-room-input pr-15'
                                            id = 'enter_room_field'
                                            name = { 'email' }
                                            onChange = { this._onRoomChange }
                                            onKeyDown = { this.handleEnter }
                                            pattern = { ROOM_NAME_VALIDATE_PATTERN_STR }
                                            title = { t('welcomepage.roomNameAllowedChars') }
                                            type = 'text'
                                            value = { this.state.room } />
                                    </div>
                                    <p className = 'enter-room-title-input'>Password</p>
                                    <div className = 'enter-room-input-container dis-flex'>
                                        <input
                                            aria-disabled = 'false'
                                            aria-label = 'Password input'
                                            className = 'enter-room-input'
                                            id = 'enter_password_field'
                                            name = { 'password' }
                                            onChange = { this._onPasswordChange }
                                            onKeyDown = { this.handleEnter }
                                            pattern = { ROOM_NAME_VALIDATE_PATTERN_STR }
                                            title = { t('welcomepage.roomNameAllowedChars') }
                                            type = { this.state.isShowPassword ? 'text' : 'password' }
                                            value = { this.state.password } />
                                        <button
                                            className = 'enter-room-button-show-password'
                                            onClick = { () => this.setState({ isShowPassword: !this.state.isShowPassword }) }>
                                            { !this.state.isShowPassword
                                                ? <IconEyeHide
                                                    height = { 15 }
                                                    width = { 15 } />
                                                : <IconEyeShow
                                                    height = { 15 }
                                                    width = { 15 } />
                                            }
                                        </button>
                                    </div>
                                    <button
                                        aria-disabled = 'false'
                                        aria-label = 'Start meeting'
                                        className = 'welcome-page-button'
                                        disabled = { !disabled }
                                        id = 'enter_room_button'
                                        onClick = { this._onFormSubmit }
                                        style = {{ backgroundColor: disabled ? '#0074E0' : '#363A40' }}
                                        tabIndex = '0'
                                        type = 'button'>
                                        {this.state.isLoading ? <Spinner /> : 'Log in'}
                                    </button>
                                    <span
                                        style = {{ color: 'red',
                                            marginTop: 10 }}>{this.state.error}</span>
                                    <div className = { `enter-room-checkbox ${this.state.isKeepLoggedIn ? 'checked' : ''}` }>
                                        <Checkbox
                                            isChecked = { this.state.isKeepLoggedIn }
                                            name = 'checkbox-default'
                                            onChange = { () => {
                                                this.setState({
                                                    isKeepLoggedIn: !this.state.isKeepLoggedIn
                                                });
                                            } }
                                            size = { 'large' } />
                                        <p
                                            className = 'enter-room-title-input'
                                            onClick = { () => {
                                                this.setState({
                                                    isKeepLoggedIn: !this.state.isKeepLoggedIn
                                                });
                                            } }>Keep me logged in</p>
                                    </div>
                                    <div><a
                                        href = 'https://dev.panthermediasystem.com/signup'
                                        rel = 'noreferrer'
                                        style = {{ color: 'black' }}
                                        target = '_blank'>Not Registered? Sign Up HERE for free</a></div>
                                </div>


                                {_moderatedRoomServiceUrl && (
                                    <div id = 'moderated-meetings'>
                                        <p>
                                            {
                                                translateToHTML(
                                                    t, 'welcomepage.moderatedMessage', { url: _moderatedRoomServiceUrl })
                                            }
                                        </p>
                                    </div>)}
                            </div>
                        }
                    </div>
                </div>
                <div
                    className = { 'dis-flex jus-center ali-center wid-full hei-full' }>
                    <IconLogoPanther width = { 500 } />
                </div>
                {/* <SessionChecking /> */}
            </div>
        );
    }


    /**
     * Renders the insecure room name warning.
     *
     * @inheritdoc
     */
    _doRenderInsecureRoomNameWarning() {
        return (
            <div className = 'insecure-room-name-warning'>
                <Icon src = { IconWarning } />
                <span>
                    { this.props.t('security.insecureRoomNameWarning') }
                </span>
            </div>
        );
    }

    handleEnter = key => {
        if (key.keyCode === 13 && this.state.room && this.state.password) {
            this._onFormSubmit(key);
        }
    };

    /**
     * Prevents submission of the form and delegates join logic.
     *
     * @param {Event} event - The HTML Event which details the form submission.
     * @private
     * @returns {void}
     */
    async createLoginEvent() {
        this.setState({
            isLoading: true
        });
        const removeWhiteSpaces = this.state.room.replace(/ /g, '');

        try {
            const data = await loginService.login(removeWhiteSpaces, this.state.password);


            // console.log(data)
            jitsiLocalStorage.setItem('isKeepLoggedIn', this.state.isKeepLoggedIn);
            jitsiLocalStorage.setItem('token', data.token);
            jitsiLocalStorage.setItem('refreshToken', data.refreshToken);

            goToPage(ROOT);
        } catch (error) {
            this.setState({
                error: error.response?.data?.message
            });
        } finally {
            this.setState({
                isLoading: false
            });
        }
    }
    _onFormSubmit(event) {
        event.preventDefault();
        if (!this._roomInputRef || this._roomInputRef.reportValidity()) {
            // this.setState({
            //     isLogin: true
            // });
            this.createLoginEvent();
        }
    }

    /**
     * Overrides the super to account for the differences in the argument types
     * provided by HTML and React Native text inputs.
     *
     * @inheritdoc
     * @override
     * @param {Event} event - The (HTML) Event which details the change such as
     * the EventTarget.
     * @protected
     */
    _onRoomChange(event) {
        super._onRoomChange(event.target.value);
        this.setState({
            error: ''
        });
    }

    _onPasswordChange(event) {
        super._onPasswordChange(event.target.value);
        this.setState({
            error: ''
        });
    }

    async getUser() {
        const user = await userService.getUserInfo();

        this.props.dispatch(setUserInfo(user));
    }

    /**
     * Callback invoked when the desired tab to display should be changed.
     *
     * @param {number} tabIndex - The index of the tab within the array of
     * displayed tabs.
     * @private
     * @returns {void}
     */
    _onTabSelected(tabIndex) {
        this.setState({ selectedTab: tabIndex });
    }

    /**
     * Renders the footer.
     *
     * @returns {ReactElement}
     */
    _renderFooter() {
        const { t } = this.props;
        const {
            MOBILE_DOWNLOAD_LINK_ANDROID,
            MOBILE_DOWNLOAD_LINK_F_DROID,
            MOBILE_DOWNLOAD_LINK_IOS
        } = interfaceConfig;

        return (<footer className = 'welcome-footer'>
            <div className = 'welcome-footer-centered'>
                <div className = 'welcome-footer-padded'>
                    <div className = 'welcome-footer-row-block welcome-footer--row-1'>
                        <div className = 'welcome-footer-row-1-text'>{t('welcomepage.jitsiOnMobile')}</div>
                        <a
                            className = 'welcome-badge'
                            href = { MOBILE_DOWNLOAD_LINK_IOS }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkIos') }
                                src = './images/app-store-badge.png' />
                        </a>
                        <a
                            className = 'welcome-badge'
                            href = { MOBILE_DOWNLOAD_LINK_ANDROID }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkAndroid') }
                                src = './images/google-play-badge.png' />
                        </a>
                        <a
                            className = 'welcome-badge'
                            href = { MOBILE_DOWNLOAD_LINK_F_DROID }>
                            <img
                                alt = { t('welcomepage.mobileDownLoadLinkFDroid') }
                                src = './images/f-droid-badge.png' />
                        </a>
                    </div>
                </div>
            </div>
        </footer>);
    }

    /**
     * Renders tabs to show previous meetings and upcoming calendar events. The
     * tabs are purposefully hidden on mobile browsers.
     *
     * @returns {ReactElement|null}
     */
    _renderTabs() {
        if (isMobileBrowser()) {
            return null;
        }

        const { _calendarEnabled, _recentListEnabled, t } = this.props;

        const tabs = [];

        if (_calendarEnabled) {
            tabs.push({
                label: t('welcomepage.calendar'),
                content: <CalendarList />
            });
        }

        if (_recentListEnabled) {
            tabs.push({
                label: t('welcomepage.recentList'),
                content: <RecentList />
            });
        }

        if (tabs.length === 0) {
            return null;
        }

        return (
            <Tabs
                onSelect = { this._onTabSelected }
                selected = { this.state.selectedTab }
                tabs = { tabs } />);
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * additional card shown near the tabs card.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalCardRef(el) {
        this._additionalCardRef = el;
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * welcome page content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the welcome page content.
     * @private
     * @returns {void}
     */
    _setAdditionalContentRef(el) {
        this._additionalContentRef = el;
    }

    /**
     * Sets the internal reference to the HTMLDivElement used to hold the
     * toolbar additional content.
     *
     * @param {HTMLDivElement} el - The HTMLElement for the div that is the root
     * of the additional toolbar content.
     * @private
     * @returns {void}
     */
    _setAdditionalToolbarContentRef(el) {
        this._additionalToolbarContentRef = el;
    }

    /**
     * Sets the internal reference to the HTMLInputElement used to hold the
     * welcome page input room element.
     *
     * @param {HTMLInputElement} el - The HTMLElement for the input of the room name on the welcome page.
     * @private
     * @returns {void}
     */
    _setRoomInputRef(el) {
        this._roomInputRef = el;
    }

    /**
     * Returns whether or not an additional card should be displayed near the tabs.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalCard() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_ADDITIONAL_CARD
            && this._additionalCardTemplate
            && this._additionalCardTemplate.content
            && this._additionalCardTemplate.innerHTML.trim();
    }

    /**
     * Returns whether or not additional content should be displayed below
     * the welcome page's header for entering a room name.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT
            && this._additionalContentTemplate
            && this._additionalContentTemplate.content
            && this._additionalContentTemplate.innerHTML.trim();
    }

    /**
     * Returns whether or not additional content should be displayed inside
     * the header toolbar.
     *
     * @private
     * @returns {boolean}
     */
    _shouldShowAdditionalToolbarContent() {
        return interfaceConfig.DISPLAY_WELCOME_PAGE_TOOLBAR_ADDITIONAL_CONTENT
            && this._additionalToolbarContentTemplate
            && this._additionalToolbarContentTemplate.content
            && this._additionalToolbarContentTemplate.innerHTML.trim();
    }
}

export default translate(connect(_mapStateToProps)(WelcomePage));
